import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User as FirebaseUser, onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import { Navigate, useLocation } from 'react-router-dom';
import { getCurrentUser } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import type { User } from '@/types/firebase';
import { getDoc, doc } from 'firebase/firestore';
import type { Role } from '@/types/firebase';
import { isCompanyAdmin } from '@/lib/permissions';
import { initKeycloak, isAuthenticated, getToken, getUserRoles, logout as keycloakLogout } from '@/services/keycloak';
import api from '@/lib/axios-config';
import { getAuthConfig, shouldEnableKeycloak, shouldEnableFirebase } from '@/config/authConfig';

interface AuthContextType {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  userRole: Role | null;
  loading: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  firebaseUser: null,
  userRole: null,
  loading: true,
  isAdmin: false,
  isSuperAdmin: false,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [userRole, setUserRole] = useState<Role | null>(null);
  const authInitializedRef = useRef(false);
  const authSourceRef = useRef<'firebase' | 'keycloak' | null>(null);

  useEffect(() => {
    if (authInitializedRef.current) {
      console.log('[AUTH] Already initialized, skipping...');
      return;
    }

    console.log('[AUTH] Starting initialization...');
    authInitializedRef.current = true;

    const initAuth = async () => {
      try {
        const authConfig = getAuthConfig();
        console.log('[AUTH] Using configuration:', authConfig);

        // Initialize Keycloak if enabled via env
        if (shouldEnableKeycloak()) {
          console.log('[AUTH] Initializing Keycloak...');
          const authenticated = await initKeycloak();

          if (authenticated) {
            const token = getToken();
            const roles = getUserRoles();

            // Check required role
            const requiredRole = 'app_invoicer';
            if (!roles.includes(requiredRole)) {
              console.log('[AUTH] User missing required role:', requiredRole);
              window.location.href = `https://app.scaleplus.gg/dashboard?error=access_denied&app=invoicer`;
              return;
            }

            if (token) {
              // Call backend to validate and get user
              const response = await api.post('/auth/keycloak-token-login', {}, {
                headers: { Authorization: `Bearer ${token}` },
              });

              if (response.data.success && response.data.user) {
                const userData = response.data.user as User;
                const customToken = response.data.customToken;

                console.log('[AUTH] User data received:', {
                  email: userData.email,
                  role: userData.role,
                  needsOnboarding: userData.needsOnboarding,
                  status: userData.status,
                  companyId: userData.companyId,
                  currentPath: window.location.pathname,
                  hasCustomToken: !!customToken
                });

                // Sign in to Firebase Auth with custom token
                // This creates a Firebase Auth user with Keycloak sub as UID
                // Makes auth.currentUser.uid work correctly for frontend code
                if (customToken) {
                  try {
                    await signInWithCustomToken(auth, customToken);
                    console.log('[AUTH] Successfully signed in to Firebase Auth with custom token');
                    console.log('[AUTH] Firebase Auth UID:', auth.currentUser?.uid);
                  } catch (error) {
                    console.error('[AUTH] Error signing in with custom token:', error);
                    // Continue anyway - user data is still valid
                  }
                }

                // Handle onboarding if needed
                if (userData.needsOnboarding && window.location.pathname !== '/keycloak-onboarding') {
                  console.log('[AUTH] Redirecting to onboarding page');
                  // Set user before redirect so RequireAuth doesn't bounce to /login
                  setUser(userData);
                  window.location.href = '/keycloak-onboarding';
                  return;
                }

                // Check if user's company is inactive (kill switch)
                if (userData?.companyId && userData.role !== 'super_admin') {
                  const companyDoc = await getDoc(doc(db, 'companies', userData.companyId));
                  if (companyDoc.exists()) {
                    const companyData = companyDoc.data();
                    if (companyData.status === 'inactive') {
                      console.log('[AUTH] Company is inactive, blocking Keycloak access');
                      keycloakLogout();
                      window.location.href = '/login?error=tenant_inactive';
                      return;
                    }
                  }
                }

                setUser(userData);
                authSourceRef.current = 'keycloak';

                // Load role if exists
                if (userData?.roleId) {
                  const roleDoc = await getDoc(doc(db, 'roles', userData.roleId));
                  if (roleDoc.exists()) {
                    setUserRole({ ...roleDoc.data(), id: roleDoc.id } as Role);
                    setIsAdmin(isCompanyAdmin(userData, roleDoc.data() as Role));
                    setIsSuperAdmin(userData?.role === 'super_admin');
                  } else {
                    setIsAdmin(userData?.role === 'admin' || userData?.role === 'super_admin');
                    setIsSuperAdmin(userData?.role === 'super_admin');
                  }
                } else {
                  setIsAdmin(userData?.role === 'admin' || userData?.role === 'super_admin');
                  setIsSuperAdmin(userData?.role === 'super_admin');
                }

                // Set isSuperAdmin flag
                setIsSuperAdmin(userData?.role === 'super_admin');
              }
            }
          } else {
            // Not authenticated - let RequireAuth handle redirect
            console.log('[AUTH] Keycloak not authenticated');
            setUser(null);
            setUserRole(null);
            setIsAdmin(false);
            setIsSuperAdmin(false);
            authSourceRef.current = null;
          }
          setLoading(false);
        }

        // Initialize Firebase if enabled via env
        if (shouldEnableFirebase()) {
          console.log('[AUTH] Initializing Firebase...');

          const unsubscribe = onAuthStateChanged(auth, async (newFirebaseUser) => {
            setFirebaseUser(newFirebaseUser);

            if (newFirebaseUser) {
              try {
                const userData = await getCurrentUser(newFirebaseUser);

                // Check if user's company is inactive (kill switch)
                if (userData?.companyId && userData.role !== 'super_admin') {
                  const companyDoc = await getDoc(doc(db, 'companies', userData.companyId));
                  if (companyDoc.exists()) {
                    const companyData = companyDoc.data();
                    if (companyData.status === 'inactive') {
                      console.log('[AUTH] Company is inactive, blocking access');
                      await auth.signOut();
                      setUser(null);
                      setFirebaseUser(null);
                      setUserRole(null);
                      setIsAdmin(false);
                      setIsSuperAdmin(false);
                      setLoading(false);
                      window.location.href = '/login?error=tenant_inactive';
                      return;
                    }
                  }
                }

                setUser(userData);
                authSourceRef.current = 'firebase';

                if (userData?.roleId) {
                  const roleDoc = await getDoc(doc(db, 'roles', userData.roleId));
                  if (roleDoc.exists()) {
                    setUserRole({ ...roleDoc.data(), id: roleDoc.id } as Role);
                    setIsAdmin(isCompanyAdmin(userData, roleDoc.data() as Role));
                    setIsSuperAdmin(userData?.role === 'super_admin');
                  } else {
                    setIsAdmin(userData?.role === 'admin' || userData?.role === 'super_admin');
                    setIsSuperAdmin(userData?.role === 'super_admin');
                  }
                } else {
                  setIsAdmin(userData?.role === 'admin' || userData?.role === 'super_admin');
                  setIsSuperAdmin(userData?.role === 'super_admin');
                }

                // Set isSuperAdmin flag
                setIsSuperAdmin(userData?.role === 'super_admin');
              } catch (error) {
                console.error('[AUTH] Error getting current user:', error);
                setUser(null);
                setUserRole(null);
                setIsAdmin(false);
                setIsSuperAdmin(false);
              }
            } else {
              // Only clear user if Firebase was the auth source
              // Don't overwrite Keycloak user in hybrid mode
              if (authSourceRef.current === 'firebase' || authSourceRef.current === null) {
                console.log('[AUTH] Clearing Firebase user');
                setUser(null);
                setUserRole(null);
                setIsAdmin(false);
                setIsSuperAdmin(false);
                authSourceRef.current = null;
              } else {
                console.log('[AUTH] Keeping Keycloak user, not clearing on Firebase auth change');
              }
            }

            setLoading(false);
          });

          return () => unsubscribe();
        } else if (!shouldEnableKeycloak()) {
          // No auth provider enabled
          console.error('[AUTH] No authentication provider enabled in environment configuration');
          setUser(null);
          setUserRole(null);
          setIsAdmin(false);
          setIsSuperAdmin(false);
          setLoading(false);
        }

      } catch (error) {
        console.error('[AUTH] Error initializing auth:', error);
        setUser(null);
        setUserRole(null);
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ user, firebaseUser, userRole, loading, isAdmin, isSuperAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useContext(AuthContext);
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // Remove the isAdmin check - all authenticated users should be allowed access to the app
  // Permission checks for specific routes are handled by the ProtectedRoute component

  return <>{children}</>;
}

// Create a separate component for admin-only routes if needed
export function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { user, loading, isAdmin } = useContext(AuthContext);
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-2">Unauthorized Access</h1>
          <p className="text-gray-600">You do not have permission to access this section.</p>
          <p className="text-gray-600">Please contact your administrator for access.</p>
          <Button
            onClick={() => {
              if (isAuthenticated()) {
                keycloakLogout();
              } else {
                auth.signOut();
              }
            }}
            className="mt-4"
            variant="outline"
          >
            Sign Out
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
